import { Menu } from '@mantine/core';
import { IconDeviceFloppy, IconDots, IconX } from '@tabler/icons-react';
import classnames from 'classnames';
import { ReactNode } from 'react';
import type { ExpandablePanelMenuItem } from './ExpandablePanel.types';

interface Props {
	title: string;
	isExpanded: boolean;
	onExpandClick: () => void;
	menuItems?: ExpandablePanelMenuItem[];
	cardActions?: ExpandablePanelMenuItem[];
	panelSummaryItems?: string[];
	children: ReactNode;
	titleItem?: ReactNode;
	dataTestId?: string;
}

export function ExpandablePanel({
	title,
	isExpanded,
	onExpandClick,
	children,
	menuItems,
	cardActions,
	panelSummaryItems,
	titleItem,
	dataTestId,
}: Props) {
	return (
		<div
			className={classnames(
				'border border-gray-300 border-solid rounded-lg box-border overflow-hidden',
				{
					'bg-gray-100 cursor-pointer hover:border-gray-400': !isExpanded,
					'bg-white': isExpanded,
				}
			)}
			onClick={() => !isExpanded && onExpandClick()}
			data-testid={dataTestId}
		>
			{isExpanded ? (
				<>
					{children}
					{Boolean(menuItems?.length) && (
						<div className="flex justify-between  p-2">
							{menuItems?.map((menuItem) => (
								<div
									key={menuItem.title}
									onClick={() => menuItem.onClick()}
									className={classnames(
										'flex items-center text-sm font-semibold justify-center py-1 px-2 cursor-pointer',
										{
											'bg-blue-600 text-white rounded':
												menuItem.intent === 'normal',
											'text-red-500': menuItem.intent === 'danger',
										}
									)}
								>
									{menuItem.intent === 'danger' && <IconX stroke={1} size={30} />}
									{menuItem.intent === 'normal' && (
										<IconDeviceFloppy stroke={1} size={30} className="mr-1" />
									)}
									{menuItem.title}
								</div>
							))}
						</div>
					)}
				</>
			) : (
				<>
					<div className="flex justify-between items-center py-2 px-4 overflow-hidden">
						<div className="font-bold text-sm mr-1">{title}</div>

						{titleItem}

						<Menu shadow="md" width={150} position="bottom-end">
							<Menu.Target>
								<IconDots
									onClick={(event) => {
										event.stopPropagation();
										event.preventDefault();
										if (!cardActions?.length) {
											onExpandClick();
										}
									}}
									stroke={1}
									size={30}
								/>
							</Menu.Target>
							{Boolean(cardActions?.length) && (
								<Menu.Dropdown>
									{cardActions?.map((cardAction) => (
										<Menu.Item
											key={cardAction.title}
											onClick={(event) => {
												event.stopPropagation();
												event.preventDefault();
												cardAction.onClick();
											}}
										>
											<div
												className={classnames(
													`text-sm py-1 px-2 hover:bg-gray-100`,
													{
														'text-red-500':
															cardAction.intent === 'danger',
													}
												)}
											>
												{cardAction.title}
											</div>
										</Menu.Item>
									))}
								</Menu.Dropdown>
							)}
						</Menu>
					</div>

					{panelSummaryItems?.length && (
						<div className="bg-white">
							{panelSummaryItems.map((summaryItem, index) => (
								<div
									key={`summary-item-${index}`}
									className="border-t border-gray-300 border-solid py-3 px-4 leading-5 text-sm"
								>
									{summaryItem}
								</div>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
}
